import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { generateMetaDataProps } from 'src/components/consumptionPage/ConsumptionMetaData/ConsumptionMetadataHelper';
import { useAppStore } from 'src/hooks/useAppStore';
import { useToggle } from 'src/hooks/useToggle';
import { fetchRentals } from 'src/services/apiRequests/RentalService';
import {
  getDisplayPlansData,
  getDisplayPlansMetaData,
  getUpgradePlansData
} from 'src/services/apiRequests/SubscriptionService';
import { Rails } from 'src/services/consumption/getRails';
import {
  BestOffering,
  PlansMetaData
} from 'src/services/responseTypes/plansMetaDataResponse';
import { purchasePlan } from 'src/services/responseTypes/purchasePlanResponse';
import { Countrylist } from 'src/services/responseTypes/singleLaunchResponse';
import {
  AssetDetails,
  CompanionAdsData,
  SinglePlayResponse
} from 'src/services/responseTypes/singlePlayResponse';
import { UsersPreferenceData } from 'src/store/interface';
import SimpleStore from 'src/store/SimpleStore';
import { flex } from 'src/styles/utils';
import { ContentLiteData } from 'src/types/consumption';
import { getPageName, mixpanelObject } from 'src/utils/analytics';
import ConvivaAnalyticHelper, {
  parentalControlConvivaErrorTrigger
} from 'src/utils/analytics/conviva/ConvivaAnalyticHelper';
import {
  commerceCTA,
  contentPropertySetter,
  playerCtaEvent,
  ribbonCTAEvent,
  screenViewEvent,
  timeEvent
} from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import { clearCtaEventsProperty } from 'src/utils/analytics/mixpanel/mixpanelHelper';
import { clearSubscriptionNudgePopupTimer } from 'src/utils/clearSubscriptionNudgePopupTimer';
import getComboOfferPath from 'src/utils/comboOfferHelper';
import { isServer, parseJsonObject } from 'src/utils/common';
import {
  ASSET_SUBTYPE,
  DEVICE_TYPES,
  USER_TYPES
} from 'src/utils/constants/AppConstants';
import { Commerce } from 'src/utils/constants/CommerceConstants';
import {
  ConsumptionPageStringEnums,
  SPAPIStateEnum
} from 'src/utils/constants/ConsumptionPagesConstants';
import {
  HTTP_HEADER_KEYS,
  HTTP_URL_PARAMS
} from 'src/utils/constants/HttpMethodsConstants';
import {
  APP_USER,
  CountryCode,
  DisplayLanguage,
  PartnerPlatforms,
  ROBOTS_CONFIG,
  UserTypes
} from 'src/utils/constants/stringConstants';
import { AssetsSubType } from 'src/utils/constants/TvShowDetailsPageConstants';
import {
  API_STATUS_TYPES,
  DEVICE_WIDTH_CONSTANTS,
  FeatureFlagConstants
} from 'src/utils/constants/utilConstants';
import {
  determineShowOrWebSeries,
  getAllEpisodeIntervals,
  getConsumptionPageType,
  showMoreClickHandler
} from 'src/utils/ConsumptionHelper/ConsumptionHelper';
import { getDayPrefix } from 'src/utils/conversions';
import { isGlobalCheck } from 'src/utils/globalCheckHelper';
import {
  getWatchHistoryDeatilsForGuestUser,
  parentaPinHelper
} from 'src/utils/playerHelper';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';
import { checkKidsCard, validateRouteParams } from 'src/utils/routeHandler';
import {
  getPlanMatchingTier,
  getTVODPrice
} from 'src/utils/TvodHelper/tvodHelper';
import { urlAutoCorrectionOnClientSide } from 'src/utils/URLAutoCorrection';

import { handleBuyClickOnBanner } from './consumptionBanner/helper';
import ConsumptionMetaData from './ConsumptionMetaData';
import ConsumptionMetaDataSkeletonLoader from './ConsumptionMetaData/ConsumptionMetaDataSkeletonLoader';
import TvodController from './TvodController/TvodController';
import VideoPlayer from './VideoPlayer';
import { PlayerPlaceholder } from './VideoPlayer/PlayerPlaceholder';
import PlayerLoadingScripts from '../../components/consumptionPage/VideoPlayer/player-loading-scripts/PlayerLoadingScripts';
import { gwapigetWithUserToken } from '../../services/apiRequests/MovieConsumptionPage/FetchUpnextServices';
import { postForSinglePlay } from '../../services/apiRequests/SinglePlayService';
import { colors } from '../../styles/colors';
import { MediaBP as MQ } from '../../styles/mediaQuery';
import {
  MIXPANEL_BUTTON_TYPE,
  MIXPANEL_CLOSE,
  MIXPANEL_ELEMENT,
  MIXPANEL_IS_RENTAL,
  MIXPANEL_PAGE_NAME,
  MIXPANEL_PLAYER,
  MIXPANEL_RIBBON,
  MIXPANEL_SOURCE,
  MIXPANEL_TAB_NAME
} from '../../utils/analytics/mixpanel/mixpanelConstants';
import {
  deleteLocalStorage,
  getLocalStorage,
  getSessionStorage,
  isIos
} from '../../utils/browserStorageHandler';
import { DynamicObject } from '../../utils/commonTypes';
import { errorCodeConstants } from '../../utils/constants/errorCodesConstants';
import { Fallback_Type_Constants } from '../../utils/constants/FallbackTypeConstants';
import {
  OPEN_PURCHASE_POPUP,
  PARTNER_NAME,
  PAYMENT_JOURNEY,
  REFERRAL_CODE,
  REFERRAL_ID,
  REFERRAL_NAME,
  REFERRAL_USERID,
  USER_TOKEN,
  USER_TYPE
} from '../../utils/constants/localStorageConstants';
import { getContentLiteDataFromSinglePlayData } from '../../utils/ConsumptionHelper/SPAPIContentLiteMappingHelper';
import { validateSinglePlayDataResponse } from '../../utils/responseValidator';
import {
  loadConvivaAdsScripts,
  loadConvivaScripts
} from '../../utils/scripts/convivaScripts';
import {
  adsScriptGenerator,
  castScriptAdder,
  loadScripts
} from '../../utils/scripts/thirdPartyScripts';
import {
  gwapiUpNextUrlGenerator,
  singlePlayApiUrlGenerator
} from '../../utils/UrlGenerator';
import { getContentAppropriateAge } from '../../utils/UtilityMethods';
import AdComponent from '../adComponent/AdComponent';
import ConsumptionBuyPlanPopup from '../ConsumptionBuyPlanPopup';
import Fallback from '../Fallback/Fallback';
import LanguageChangePopup from '../LanguageChangePopup';
import SEOMeta from '../pageMeta/SEOMeta';
import SEOMetaForLatest from '../pageMeta/SEOMetaForLatest';
import { SeoMetaObject } from '../pageMeta/SEOMetaTypes';
import {
  dateConversionObj,
  getActorsName,
  getBreadCrumbs,
  getDirectorOrActor,
  getGenres,
  getHeaderForSEO,
  getSeoData,
  getSeoUrl,
  isCountryDeOrRu,
  newKeysList,
  replaceMetaData
} from '../pageMeta/SEOMetaUtils';
import { transformToCamelCase } from '../pageMeta/transformToCamelCase';
import { getAutomaticAgeRating } from '../parental-control/parentalControlHelper';
import SetUpParentalPin from '../parental-control/SetUpParentalPin';
import ScrollToTopButton, {
  scrollToTop
} from '../scrollToTopButton/ScrollToTopButton';
import SeoContent from '../seoContent/SeoContent';
import SEOContentSkeletonLoader from '../seoContent/SEOContentSkeletonLoader';
import SeoDescription from '../seoContent/SeoDescription';
import SEODescriptionSkeletonLoader from '../seoContent/SEODescriptionSkeletonLoader';
import { ContentTypes, ThumbnailTypes } from '../thumbnail/types';

// Lazy loading components

const SubscriptionAVODNudge = dynamic(
  () => import('../SubscriptionPopUpNudge/SubscriptionAVODNudge'),
  {
    ssr: false
  }
);

const RecommendedContentsWrapper = dynamic(
  () => import('./RecommendedContents/RecommendedWrapper'),
  {
    ssr: false
  }
);

const SkeletonSimilarContentTray = dynamic(
  () => import('../SkeletonLoader/SkeletonSimilarContentTray'),
  {
    ssr: true
  }
);
const RelatedContents = dynamic(() => import('./RelatedContents'), {
  loading: () => <SkeletonSimilarContentTray />
});
const PlexKnowMoreWidget = dynamic(
  () => import('../plexKnowMoreWidget/PlexKnowMoreWidget'),
  {
    ssr: false
  }
);
const ComboOfferWidget = dynamic(
  () => import('../comboOfferWidget/ComboOfferWidget'),
  {
    ssr: false
  }
);
const ChromeVersionPopup = dynamic(() => import('../ChromeVersionPopup'), {
  ssr: false
});

const CommerceDrawerPopUp = dynamic(
  () => import('../commerce/CommerceDrawerPopup'),
  {
    ssr: false
  }
);

interface ConsumptionPageProps {
  pageDetails: {
    locale?: string;
    contentType: ContentTypes;
    showId?: string;
    episodeId?: string;
    episodeName?: string;
    seasonId?: string;
    isLatestRoute?: boolean;
    isLatest1Route?: boolean;
  };
  contentLiteData?: ContentLiteData;
  seoMetaObject?: SeoMetaObject;
  botRails?: any;
  seoMetaAssets?: any;
  originalTitle?: string;
}
interface UpnextParams {
  type?: string;
  asset_id?: string;
  translation?: string;
  languages?: string | undefined;
  country?: string;
  pc_age?: any;
}

export interface PaymentProvider {
  name: string;
}

type Nullable<T> = T | null;

const ConsumptionComponentContainer = styled.div`
  ${flex('column', 'flex-start', 'stretch')}
  color: ${colors.GrayD8};
`;

const ConsumptionWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  overflow-y: auto;
`;

const MainSection = styled.section`
  ${flex('row', 'space-between', 'flex-start')};
  ${MQ.MediaLg} {
    padding: 0px 40px 0px 40px;
  }
  ${MQ.MediaXl} {
    padding: 0px 100px 0px 100px;
  }
  width: 100%;
`;

const PlayerAndMetaData = styled.div`
  ${flex('column', 'space-between', 'stretch')}
  width: 100%;
  ${MQ.MediaLg} {
    width: 64.5%;
  }
  ${MQ.MediaXl} {
    width: 69.5%;
  }
`;

const Container = styled.div`
  ${MQ.MediaSm} {
    padding: 0px 16px 0px 15px;
  }
  ${MQ.MediaMd} {
    padding: 0px 40px 0px 40px;
  }
  ${MQ.MediaLg} {
    padding: 0px 40px 0px 40px;
  }
  ${MQ.MediaXl} {
    padding: 0px 100px 0px 100px;
  }
`;

const MetaDataContainer = styled.div<any>`
  ${MQ.MediaSm} {
    padding: 0px 16px 0px 15px;
  }
  ${MQ.MediaMd} {
    padding: 0px 40px 0px 40px;
  }
  ${MQ.MediaScreenMdMaxPortrait} {
    padding-top: ${(props) =>
      props.isRequestedByBot
        ? 0
        : props.isGuestUser
        ? 'calc(56.25% + 48px)'
        : props.playerWrapperHeight + 'px'};
  }
  ${MQ.MediaScreenLgMaxPortrait} {
    padding-top: ${(props) =>
      props.isRequestedByBot
        ? 0
        : props.isGuestUser
        ? 'calc(56.25% + 94px)'
        : props.playerWrapperHeight + 'px'};
  }
  ${MQ.MediaLg} {
    padding: 0px;
  }
`;

const PlayerWrapper = styled.div<any>`
  ${MQ.MediaScreenLgMaxPortrait} {
    width: 100%;
    position: ${(props) => (props.isRequestedByBot ? 'relative' : 'fixed')};
    z-index: 100;
  }
`;

const ConsumptionPage: React.FC<ConsumptionPageProps> = ({
  pageDetails,
  contentLiteData: assetDetailsFromServer,
  seoMetaAssets: seoMetaAssetsFromServer,
  seoMetaObject,
  botRails
}) => {
  SimpleStore.set(ROBOTS_CONFIG, {
    noFollowNoIndex: seoMetaObject?.noIndexNoFollow || false,
    noIndex: seoMetaObject?.noIndex || false,
    noFollow: seoMetaObject?.noFollow || false,
    contentVersion: assetDetailsFromServer?.extended?.content_version || ''
  });
  const [singlePlayDetailsMovie, setSinglePlayDetailsMovie] =
    useState<SinglePlayResponse>();
  const [seoMetaAssetsClient, setSeoMetaAssetsClient] =
    useState<Nullable<DynamicObject>>(null);

  const seoMetaAssets = seoMetaAssetsFromServer ?? seoMetaAssetsClient;
  const contentLiteData: ContentLiteData | undefined =
    assetDetailsFromServer ??
    (singlePlayDetailsMovie &&
      getContentLiteDataFromSinglePlayData(singlePlayDetailsMovie));

  const { contentType, showId, isLatestRoute, isLatest1Route } =
    pageDetails ?? {
      contentType: '',
      showId: '',
      isLatestRoute: false,
      isLatest1Route: false
    };

  const {
    title: contentTitle,
    description: contentDescription,
    genres: contentGenres,
    languages: contentLanguages,
    audio_languages: audioLanguages,
    actors: contentActors,
    asset_subtype: assetSubType
  } = contentLiteData ?? {
    id: '',
    title: '',
    genres: [],
    languages: [],
    audio_languages: [],
    actors: [],
    assetSubType: ''
  };

  const {
    translations,
    usersLocationData,
    usersPreferenceData,
    appConfig,
    initialLaunchData,
    tvodRentals,
    remoteConfig,
    popups,
    setPopups,
    plansMetaData,
    usersData,
    comboOfferState,
    setTvodRentals,
    tvodState,
    setTvodState,
    setUsersPreferenceData,
    companionAdState,
    setCompanionAdState,
    loadExternalScripts,
    setExternalScripts,
    setCommerceComponentData,
    commerceData,
    subscriptionData = {
      currentActiveSubscriptions: [],
      maxPricePack: {}
    },
    isUserLoggedIn,
    setComboOfferState,
    setBestOfferingPlan
  } = useAppStore();

  const allLang = initialLaunchData?.getlang?.content || [];

  const isRequestedByBot = appConfig.isRequestedByBot as boolean;

  const deviceType = appConfig.deviceType;

  const router = useRouter();

  /**
   * Consumption component is common btw differnt types of content (Movie, Tv-shows, Web-series etc)
   * Here we are not depending SP-API on Gwapi result and hence reading contentId from URl
   * @returns contentID
   */
  const getContentIdFromURL = () => {
    switch (pageDetails.contentType) {
      case ContentTypes.movies:
        return router.query.movieId;
      case ContentTypes.tvshows:
      case ContentTypes.webSeries:
        return isLatestRoute || isLatest1Route
          ? showId
          : router.query.episodeId;
    }
  };
  const userType = getLocalStorage(USER_TYPE) || UserTypes.GUEST;
  const contentIdFromUrl = validateRouteParams(getContentIdFromURL() as string);
  const [isUpNextPending, setIsUpNextPending] = useState(false);
  const [upNextApiState, setUpNextApiState] = useState(false);
  const [playingStarted, setPlayingStarted] = useState(false);
  const [upNextMovies, setUpNextMovies] = useState([]);
  const consumptionPageAudioLanguages = audioLanguages?.join(',') ?? '';
  const countryCode = usersLocationData?.country_code || CountryCode.IN;
  const stateCode = usersLocationData?.state_code;
  const displayLanguages =
    usersPreferenceData?.display_language || DisplayLanguage.ENGLISH;
  const userSelectedContentLanguage = usersPreferenceData?.content_language;
  const [playerSectionHeight, setPlayerSectionHeight] = useState<number>(0);
  const [commerceLayout, setCommerceLayout] = useState('');
  const [showCrew, toggleCrew] = useToggle(false);
  const playerSectionRef = useRef<any>();
  const [playerWrapperHeight, setPlayerWrapperHeight] = useState<number>(0);
  const countrylist: Countrylist | undefined =
    initialLaunchData?.countrylist?.[0];
  const isOnAirShow =
    singlePlayDetailsMovie?.assetDetails?.on_air == 'true' ? true : false;

  const [railsData, setRailsData] = useState<any>({
    similarContents: null,
    recommendedContents: null,
    storiesContents: null,
    otherCollection: botRails?.otherCollection || [],
    status: API_STATUS_TYPES.idle
  });

  const [contentInstance, setContentInstance] = useState<Rails>();

  const [retryPlansMetaData, setPlansMetaData] = useState<PlansMetaData[]>();
  const [showPlexRentNowPopUp, setPlexRentNowPopUp] = useState(false);
  const [chromeVersionPopupEnabled, setChromeVersionPopupEnabled] =
    useState<boolean>(false);
  const [shouldShowBuyPlanPopup, setBuyPlanPopup] = useState(false);
  const [selectedAgeValue, setSelectedAgeValue] = useState<
    string | undefined
  >();
  const [showSetParentalPinPopup, setShowSetParentalPinPopup] =
    useState<boolean>(false);
  const [isViTvod, setVITvod] = useState(false);
  const [isRibbonClicked, setRibbonClicked] = useState<boolean>(false);
  const [showToastMsg, toggleToastMsg] = useState<boolean>(false);

  const contentId =
    contentLiteData?.id || singlePlayDetailsMovie?.assetDetails?.id || '';

  const seasonId =
    contentLiteData?.seasonId ||
    singlePlayDetailsMovie?.assetDetails?.season ||
    '';

  // will be used for loading the page meta data and seo content table
  const [spAPIState, setSpAPIState] = useState<SPAPIStateEnum>(
    SPAPIStateEnum.UNINITIALIZED
  );
  const [showWishlist, setShowWishlist] = useState<boolean>(false);
  const [isContentUnavailable, setIsContentUnavailable] =
    useState<boolean>(false);

  const getSinglePlayDetails = async (spApiObject?: any) => {
    let contentId = contentIdFromUrl;
    // carries all the details for the subsequent calls made once the single play api is success
    if (window && typeof window.storeMixPanelMetricsInSS == 'function') {
      window.storeMixPanelMetricsInSS('SPAPI_Request_Time_1', Date.now());
    }

    let parentalPinStatus = true;
    if (
      spApiObject &&
      Object.keys(spApiObject).length > 0 &&
      spApiObject.parentalPinFlag
    ) {
      parentalPinStatus = spApiObject.parentalPinStatus;
      contentId = spApiObject.contentId;
    }
    // eslint-disable-next-line prefer-const
    let { isParentalControlSet, parentalControlValue, parentalControlPin } =
      // getSettingsData in zee5-pwa
      await parentaPinHelper(
        usersPreferenceData as UsersPreferenceData,
        userType,
        countrylist as Countrylist,
        parentalPinStatus
      );
    contentId =
      spApiObject?.assetSubType === ASSET_SUBTYPE.TRAILER
        ? spApiObject.id
        : contentLiteData?.id &&
          (contentType === ContentTypes.tvshows ||
            contentType === ContentTypes.webSeries)
        ? contentLiteData?.id
        : contentId;

    const contentObj = {
      /**
       * If content is latest and not trailer then we will take showId, else we will take contentId.
       * Suppose If latest is playing then both the conditions will satisfy and showId will take,
       * after some time if user clicks on trailer then assetSubType will be trailer then
       * we will take contentId
       */
      id:
        spApiObject?.assetSubType !== ASSET_SUBTYPE.TRAILER &&
        (isLatestRoute || isLatest1Route)
          ? showId
          : contentId,
      country:
        usersLocationData?.country_code ||
        SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE) ||
        CountryCode.IN,
      isVmax: false,
      region: usersLocationData?.state_code || '',
      translation: displayLanguages,
      languages: contentLanguages,
      userType: getLocalStorage(USER_TYPE) || UserTypes.GUEST,
      contentId: contentId,
      showId: showId,
      isLoggedIn: !!getLocalStorage(USER_TOKEN),
      gender: usersData?.gender,
      user: getSessionStorage(PARTNER_NAME),
      age: getUsersAge(usersData?.birthday),
      isParentalControlSet: false,
      isLatestRoute: isLatestRoute,
      isLatest1Route: isLatest1Route
    };

    if (selectedAgeValue) {
      parentalControlValue = selectedAgeValue;
    }

    const url = await singlePlayApiUrlGenerator({
      ...contentObj,
      isParentalControlSet,
      parentalControlValue
    });
    setSpAPIState(SPAPIStateEnum.IN_PROGRESS);

    try {
      const requestObj = {
        'x-access-token': SimpleStore.get(HTTP_HEADER_KEYS.X_ACCESS_TOKEN)
      };
      const response = await postForSinglePlay(url, requestObj);
      const errorCode = response?.error_code;
      if (
        errorCode == errorCodeConstants.DATA_NOT_FOUND ||
        errorCode == errorCodeConstants.CONTENT_NOT_AVAILABLE_IN_YOUR_REGION
      ) {
        setIsContentUnavailable(true);
      }
      if (response && response.error_code === '3608') {
        if (window && typeof window.storeMixPanelMetricsInSS == 'function') {
          window.storeMixPanelMetricsInSS('SPAPI_Response_FAILED', Date.now());
        }

        // await addAndRemoveDevice();
        // data = await postForSinglePlay({url});
      }
      if (
        response &&
        response.error_code === '401' &&
        getLocalStorage(USER_TOKEN)
      ) {
        if (window && typeof window.storeMixPanelMetricsInSS == 'function') {
          window.storeMixPanelMetricsInSS('SPAPI_Response_FAILED', Date.now());
        }
        // renewUserToken();
        // alert('401')
        // setSinglePlayDetailsMovie(response);
      } else if (validateSinglePlayDataResponse(response)) {
        if (window && typeof window.storeMixPanelMetricsInSS == 'function') {
          window.storeMixPanelMetricsInSS('SPAPI_Response_Time_1', Date.now());
        }

        response.parentalControlPin = parentalControlPin;
        if (
          userType === UserTypes.GUEST &&
          response.assetDetails &&
          response.assetDetails.id
        ) {
          if (getWatchHistoryDeatilsForGuestUser(response)) {
            response.watchHistoryDetails =
              getWatchHistoryDeatilsForGuestUser(response);
          }
        }
        setSinglePlayDetailsMovie(response);
      }
    } catch (error) {
      const errorCode = (error as any)?.responseData?.error_code;
      if (
        errorCode == errorCodeConstants.DATA_NOT_FOUND ||
        errorCode == errorCodeConstants.CONTENT_NOT_AVAILABLE_IN_YOUR_REGION
      ) {
        setIsContentUnavailable(true);
      }
      toggleToastMsg(true);
      if (window && typeof window.storeMixPanelMetricsInSS == 'function') {
        window.storeMixPanelMetricsInSS('SPAPI_Response_FAILED', Date.now());
      }
      console.error('SinglePlay API failure: ', error);
    } finally {
      setSpAPIState(SPAPIStateEnum.FINISHED);
    }
  };

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const playerComponent = document.querySelector<HTMLElement>(
        '[data-testid ="playerWrapper"]'
      );
      if (playerComponent) {
        const playerWrapperObservable = new ResizeObserver((entries) => {
          setPlayerWrapperHeight(entries[0]?.contentRect?.height);
        });
        playerWrapperObservable.observe(playerComponent);
        return () => {
          playerWrapperObservable.unobserve(playerComponent);
        };
      }
    }
  }, [playerWrapperHeight]);

  const userSettingsValue = usersPreferenceData;
  const parentalData = parseJsonObject(userSettingsValue?.parental_control_v2);
  const automaticPinMandatoryPopup =
    getLocalStorage(USER_TOKEN) &&
    getAutomaticAgeRating(countrylist) &&
    !(parentalData?.age_rating && parentalData?.pin) &&
    singlePlayDetailsMovie?.error_code === '606';

  useEffect(() => {
    if (
      isUserLoggedIn &&
      subscriptionData?.maxPricePack?.subscription_plan?.id &&
      singlePlayDetailsMovie &&
      comboOfferState?.isComboOfferEnabled
    ) {
      const singleTvodPlan = getTvodPlanDetails(
        singlePlayDetailsMovie?.assetDetails?.tier
      );
      if (singleTvodPlan) {
        getUpgradePlans(singleTvodPlan[0]?.id);
      }
    }
  }, [
    subscriptionData,
    singlePlayDetailsMovie,
    comboOfferState?.isComboOfferEnabledPending
  ]);

  const getUpgradePlans = async (combo_plan_id: string | undefined) => {
    const upgradePlans = await getUpgradePlansData(
      subscriptionData?.maxPricePack?.subscription_plan?.id,
      combo_plan_id
    );
    if (upgradePlans?.plans?.length) {
      const { plans } = upgradePlans;
      const upgradePlanData = plans.filter(
        (item) => item?.purchase_allowed === true
      );
      if (!upgradePlanData.length) {
        setComboOfferState({
          isComboOfferEnabledPending: false,
          isComboOfferEnabled: false
        });
      }
    }
  };
  const getTvodPlanDetails = (tierName: string | undefined) => {
    if (tierName && tvodRentals?.tvodPlans) {
      const planMatchingTier: purchasePlan[] = getPlanMatchingTier(
        tierName,
        tvodRentals.tvodPlans
      );
      return planMatchingTier;
    }
  };

  useEffect(() => {
    if (!showSetParentalPinPopup && automaticPinMandatoryPopup) {
      setShowSetParentalPinPopup(true);
    }
  }, [automaticPinMandatoryPopup]);

  const toggleTeaserSubscriptionPopup = (isClicked: boolean) => {
    if (isClicked) handleBuyClickOnBanner(remoteConfig);
    // setBuyPlanPopup(isClicked);
  };

  const getUsersAge = (dob: string | undefined) => {
    if (dob) {
      return new Date().getFullYear() - new Date(dob).getFullYear();
    }
    return '';
  };

  const initializeConviva = () => {
    if (window.Conviva) {
      if (window.convivaAnalytic) {
        return;
      }
      const isAppAnalyticsEnabled = isFeatureEnabled(
        remoteConfig,
        FeatureFlagConstants.FEATURE_CONVIVA_APP_ANALYTICS
      );

      window.convivaAnalytic = ConvivaAnalyticHelper;
      ConvivaAnalyticHelper.initializeConviva(isAppAnalyticsEnabled as boolean);
    } else {
      setTimeout(initializeConviva, 200);
    }
  };

  const triggerOnExit = true;

  useEffect(() => {
    const contentExitHandler = () => {
      if (!triggerOnExit) return;
      const playbackended = window.contentExit;
      if (playbackended) {
        window.contentExit = '';
        playbackended();
      }
      parentalControlConvivaErrorTrigger(ConvivaAnalyticHelper);
    };
    const handleWindowClose = (e: any) => {
      contentExitHandler();
      return e;
    };
    const handleBrowseAway = () => {
      contentExitHandler();
      return;
    };
    window.addEventListener('beforeunload', handleWindowClose);
    router.events.on('routeChangeStart', handleBrowseAway);
    return () => {
      window.removeEventListener('beforeunload', handleWindowClose);
      router.events.off('routeChangeStart', handleBrowseAway);
    };
  }, [router.events, triggerOnExit]);

  /**
   * This is required to open rent now pop-up directly for IOS-TVOD
   * based on the localstorage value, set in _app.tsx
   */
  useEffect(() => {
    if (
      tvodState?.isTvodContent &&
      getLocalStorage(OPEN_PURCHASE_POPUP) === 'true' &&
      !comboOfferState?.isComboOfferEnabledPending
    ) {
      deleteLocalStorage(OPEN_PURCHASE_POPUP);
      handleRentNowPopUp();
    }
  }, [tvodState?.isTvodContent, comboOfferState?.isComboOfferEnabledPending]);

  // Fetch relavant rails after component is mounded
  useEffect(() => {
    const referralId = getLocalStorage(REFERRAL_ID);
    if (referralId) {
      deleteLocalStorage(REFERRAL_ID);
      deleteLocalStorage(REFERRAL_NAME);
      deleteLocalStorage(REFERRAL_CODE);
      deleteLocalStorage(REFERRAL_USERID);
    }
    async function getPlans() {
      if (!plansMetaData?.length) {
        const displayedPlansMetaData: PlansMetaData[] =
          await getDisplayPlansMetaData();
        const timeout = 8 * 1000;
        const displayPlansResponse = await getDisplayPlansData(timeout);
        setPlansMetaData(displayedPlansMetaData);
        setBestOfferingPlan(
          displayPlansResponse?.best_offering as BestOffering
        );
      }
    }
    getPlans();
  }, []);

  useEffect(() => {
    if (!isRequestedByBot) {
      if (!document.querySelector('#loadCastFramework') && !isIos()) {
        castScriptAdder();
      }
      loadConvivaScripts();
      loadConvivaAdsScripts();

      setTimeout(() => {
        initializeConviva();
      }, 500);
    }
  }, []);

  useEffect(() => {
    setPlayingStarted(false);
  }, [contentIdFromUrl]);

  useEffect(() => {
    if (!assetDetailsFromServer) {
      (async () => {
        const assets = await getSeoData(countryCode, displayLanguage);
        setSeoMetaAssetsClient(assets);
      })();
    }
  }, [contentId]);

  const getRailsData = async () => {
    const getActorsFromArray =
      contentActors &&
      contentActors.length > 0 &&
      contentActors.map((str: string) => str?.split(':')[0])?.join(',');
    setRailsData((prevState: any) => ({
      ...prevState,
      status: API_STATUS_TYPES.loading
    }));
    const requestObj = {
      contentId: contentIdFromUrl,
      contentTitle,
      country: countryCode as string,
      languages: userSelectedContentLanguage as string,
      translation: displayLanguages as string,
      region: stateCode as string,
      contentSupportedLanguages: contentLanguages as string[],
      contentGenre: contentGenres as any[],
      seasonId: seasonId,
      episodeId: contentId as string,
      showId: showId as string,
      actors: getActorsFromArray as string,
      isRequestedByBot: isRequestedByBot,
      onAir: isOnAirShow,
      requestedFromClient:
        appConfig.isRequestedByBot && !railsData?.otherCollection?.length,
      allLang,
      assetType: contentLiteData?.asset_type,
      assetSubType
    };
    const contentInstance = Rails.getContentRailInstance(
      contentType,
      requestObj,
      translations
    );
    setContentInstance(contentInstance);

    try {
      if (isRequestedByBot && contentType !== ContentTypes.movies) {
        const recommendedContents =
          await contentInstance.contentInstance.fetchRecommendedContents();
        return setRailsData({
          similarContents: botRails.similarContents,
          recommendedContents,
          otherCollection: botRails.otherCollection,
          status: API_STATUS_TYPES.success
        });
      }
      const {
        similarContents,
        recommendedContents,
        otherContents,
        storiesContents
      } = await contentInstance.fetchRailContents(remoteConfig);

      setRailsData({
        similarContents,
        recommendedContents,
        storiesContents,
        otherCollection: !isRequestedByBot
          ? otherContents
          : botRails.otherCollection,
        status: API_STATUS_TYPES.success
      });
    } catch (error) {
      setRailsData((prevState: any) => ({
        ...prevState,
        status: API_STATUS_TYPES.failure
      }));
    }
  };
  const isAdPlayingOnPlayer = companionAdState?.isAdPlayingOnPlayer;

  useEffect(() => {
    if (
      (singlePlayDetailsMovie && playingStarted) ||
      (singlePlayDetailsMovie && isAdPlayingOnPlayer) ||
      singlePlayDetailsMovie?.error_code ||
      isRequestedByBot
    ) {
      if (railsData?.status === API_STATUS_TYPES.idle) {
        contentPropertySetter(
          mixpanelObject,
          singlePlayDetailsMovie?.assetDetails,
          {}
        );

        getRailsData();
      }
    }
  }, [
    isAdPlayingOnPlayer,
    playingStarted,
    singlePlayDetailsMovie,
    railsData?.status,
    translations,
    userSelectedContentLanguage
  ]);

  useEffect(() => {
    if (railsData.status === API_STATUS_TYPES.success) {
      // Reset rails data
      setRailsData({
        similarContents: null,
        recommendedContents: null,
        otherCollection: [],
        status: API_STATUS_TYPES.idle
      });
    }
    setCompanionAdState({
      companionAdLoaded: false,
      isAdPlayingOnPlayer: false
    });
    setTvodState({
      isTvodContent: false,
      tvodPriceObj: null,
      planDetails: null,
      isTvodEvent: false,
      tvodOverlay: false,
      isTVODLogicResolved: false,
      isUserEntitledForTvod: false
    });
  }, [router.asPath, userSelectedContentLanguage, displayLanguages]);

  useEffect(() => {
    scrollToTop();
  }, []);

  const comboOfferBannerClickMixpanelHandler = () => {
    clearCtaEventsProperty(mixpanelObject);
    mixpanelObject[MIXPANEL_TAB_NAME] = getPageName(router.asPath);
    mixpanelObject[MIXPANEL_BUTTON_TYPE] = MIXPANEL_RIBBON;
    mixpanelObject[MIXPANEL_ELEMENT] =
      translations.plex.TVOD_CONSUMPTION.Player_Rent_CTA;
    mixpanelObject[MIXPANEL_IS_RENTAL] = comboOfferState.isComboOfferEnabled;
    ribbonCTAEvent();
  };

  const handleRentNowPopUp = (isRibbonClick = false) => {
    // rent for - player - cta
    // rent for tvod widget - cta
    // rent for player - ribbon
    setRibbonClicked(isRibbonClick);

    const { partner_name: partnerName = '' } =
      usersPreferenceData?.partner || {};

    const partnerFlag = partnerName === PartnerPlatforms.VIMTV;

    if (comboOfferState.isComboOfferEnabled) {
      comboOfferBannerClickMixpanelHandler();
      // in case of trailer - original ID is stored in "primaryMediaId"
      //This redirection is from teaser banner, if combo offer is enabled.
      const offerPath =
        singlePlayDetailsMovie &&
        singlePlayDetailsMovie.assetDetails &&
        getComboOfferPath(singlePlayDetailsMovie.assetDetails.id);
      if (partnerFlag) {
        setVITvod(true);
      } else {
        (window as any).location.href = offerPath;
      }
    } else {
      setPlexRentNowPopUp(!showPlexRentNowPopUp);
    }
  };

  useEffect(() => {
    if (showPlexRentNowPopUp) {
      const { partner_name: partnerName = '' } =
        usersPreferenceData?.partner || {};

      const partnerFlag = partnerName === PartnerPlatforms.VIMTV;

      const tireNode =
        singlePlayDetailsMovie?.assetDetails &&
        Object.keys(singlePlayDetailsMovie.assetDetails).length
          ? singlePlayDetailsMovie.assetDetails.tier
          : '';

      const tvodTierNode = countrylist ? countrylist.tvod_tiers : {};

      const tvodPrice: any =
        tireNode && tvodTierNode ? getTVODPrice(countrylist, tireNode) : {};

      mixpanelObject[MIXPANEL_BUTTON_TYPE] = isRibbonClicked
        ? MIXPANEL_RIBBON
        : MIXPANEL_PLAYER;

      mixpanelObject[MIXPANEL_ELEMENT] =
        translations?.plex?.TVOD_CONSUMPTION?.DetailsPage_PlayerUnit_RentNow_Button.replace(
          '{{currency}}',
          tvodPrice && tvodPrice.currency
        ).replace('{{amount}}', tvodPrice && tvodPrice.price);

      mixpanelObject[MIXPANEL_TAB_NAME] = getPageName(router.asPath);
      handleRentNowPlayerCTA(partnerFlag);
    } else {
      // TODO: Make drower open
      // this.setState({ isDrowerOpen: true });
    }
  }, [showPlexRentNowPopUp]);

  function handleRentNowPlayerCTA(partnerFlag: boolean) {
    // player cta - Rent FOR
    if (isRibbonClicked) {
      // Ribbon click flag need to add here
      ribbonCTAEvent();
    } else {
      playerCtaEvent();
    }

    if (partnerFlag) {
      setVITvod(true);
      if (showPlexRentNowPopUp) {
        setPlexRentNowPopUp(!showPlexRentNowPopUp);
      }
    }
  }

  useEffect(() => {
    //Mixpanel events for screen view event
    const pageName = getPageName(router.asPath);
    mixpanelObject[MIXPANEL_SOURCE] = pageName;
    mixpanelObject[MIXPANEL_PAGE_NAME] = pageName;
    mixpanelObject[MIXPANEL_TAB_NAME] = pageName;
    screenViewEvent();
  }, [router.asPath]);

  useEffect(() => {
    if (
      usersPreferenceData?.parental_control_v2 &&
      typeof usersPreferenceData.parental_control_v2 === 'string'
    ) {
      // const parentalControlV2 = JSON.parse(
      //   usersPreferenceData.parental_control_v2
      // );
      const globalCheck = getAutomaticAgeRating(countrylist);
      if (globalCheck) {
        setSelectedAgeValue(`${globalCheck}`);
      }
      // setSelectedAgeValue(
      //   parentalControlV2.age_rating || (globalCheck && `${globalCheck}`)
      // );
    }
  }, [usersPreferenceData || selectedAgeValue]);

  useEffect(() => {
    if (!isRequestedByBot) {
      // Reset internal states on route change
      toggleToastMsg(false);
      setSinglePlayDetailsMovie(undefined);
      getSinglePlayDetails();
    }
  }, [router.asPath]);

  const replaceFromArray = [
    '<Movie-name>',
    '<genre>',
    '<cast-names>',
    '<director-name>',
    '<Show Name>',
    '<Episode Title>',
    '<Season No>',
    '<channel name>',
    '<Episode Number>',
    '<Season Title>',
    '<date>',
    '<Star Cast Names>',
    '<Show Name>',
    '<Season Title>',
    '<channel-name>'
  ];

  const tag = ConsumptionPageStringEnums.MOVIES;

  const consumptionPageTranslations = translations.consumption;
  const isKidsData = checkKidsCard(contentGenres);

  let movieTitles = '';
  let genre = '';
  let director = '';
  let actors = '';
  let showName = '';
  let creativeTitle = '';
  let season = '';
  let channelsName = '';
  let episodeNumber = '';
  let seasonTitle = '';
  let date = '';
  const channelName =
    singlePlayDetailsMovie?.assetDetails?.content_partner_details
      ?.content_partner_name ||
    contentLiteData?.content_owner ||
    '';
  const releaseDate = contentLiteData?.release_date
    ? dateConversionObj(String(contentLiteData.release_date))
    : singlePlayDetailsMovie?.assetDetails?.release_date
    ? dateConversionObj(
        String(singlePlayDetailsMovie?.assetDetails?.release_date)
      )
    : '';
  if (releaseDate) {
    const prefix = releaseDate.day ? getDayPrefix(releaseDate.day) : '';
    date = `${releaseDate.day}${prefix} ${releaseDate?.fullMonthString} ${releaseDate.year}`;
  }

  if (contentLiteData && Object.keys(contentLiteData).length > 0) {
    const meta = contentLiteData;
    movieTitles = isCountryDeOrRu(countryCode, displayLanguages)
      ? meta.title || meta.original_title
      : meta.creative_title || meta.original_title || meta.title;
    genre = getGenres(meta.genres);
    director = getDirectorOrActor(meta.directors);
    actors = getActorsName(meta.actors);
    showName = meta?.showDetails?.original_title || meta?.tvshow_name;
    creativeTitle = meta?.title;
    if (meta.season_details && meta.season_details.length > 0) {
      season =
        meta.season_details[
          meta?.season_details?.length - 1
        ]?.orderid.toString();
    }
    channelsName =
      meta?.showDetails?.channels?.length &&
      meta?.showDetails?.channels[0]?.title;

    episodeNumber = meta?.episodeNumber?.toString() ?? '';

    const seasonId = (meta as DynamicObject)?.season;
    seasonTitle =
      (meta?.season_details?.length &&
        meta?.season_details[meta?.season_details?.length - 1]?.title) ||
      meta?.showDetails?.seasons?.find(
        (item: DynamicObject) => item.id === seasonId
      )?.title;
  } else if (
    singlePlayDetailsMovie &&
    Object.keys(singlePlayDetailsMovie).length > 0
  ) {
    showName =
      singlePlayDetailsMovie?.showDetails?.title ||
      singlePlayDetailsMovie?.showDetails?.original_title;
    episodeNumber = singlePlayDetailsMovie?.assetDetails?.orderid.toString();
  }

  const replaceToArray = [
    movieTitles,
    genre,
    actors,
    director,
    showName,
    creativeTitle,
    season,
    channelsName,
    episodeNumber,
    seasonTitle,
    date,
    actors,
    showName,
    seasonTitle,
    channelName
  ];

  const displayLanguage =
    router.locale ||
    usersPreferenceData?.display_language ||
    DisplayLanguage.ENGLISH;
  const allDisplayLanguages = initialLaunchData?.getlang?.display || [];

  const seoUrl = getSeoUrl(
    contentType,
    router.asPath,
    countryCode,
    !!isLatestRoute,
    !!isLatest1Route,
    (singlePlayDetailsMovie?.assetDetails as DynamicObject) || contentLiteData,
    isKidsData
  );

  const staticMetaData =
    seoUrl && seoMetaAssets && Object.keys(seoMetaAssets).length
      ? seoMetaAssets[seoUrl]
      : null;

  const metaDataType = {
    /** If content is latest or latest1 title will read from only seo_meta.json file */
    title:
      isLatestRoute ||
      isLatest1Route ||
      router?.asPath?.includes('add-ons') ||
      assetSubType === AssetsSubType.WEBSERIES ||
      assetSubType === AssetsSubType.TVSHOW
        ? staticMetaData?.title || contentTitle
        : contentLiteData?.extended?.seo_title ||
          staticMetaData?.title ||
          contentTitle ||
          '',
    description:
      router?.asPath?.includes('add-ons') ||
      assetSubType === AssetsSubType.WEBSERIES ||
      assetSubType === AssetsSubType.TVSHOW
        ? staticMetaData?.description
        : contentLiteData?.extended?.seo_description ||
          staticMetaData?.description ||
          contentDescription ||
          ''
  };

  const metaData = replaceMetaData({
    metaDataObj: metaDataType,
    languages: allDisplayLanguages,
    languageCode: displayLanguage,
    replaceFromArray: replaceFromArray,
    replaceToArray: replaceToArray
  });
  const getRentalsData = async () => {
    const token = getLocalStorage(USER_TOKEN);
    if (
      token &&
      !tvodRentals?.getPlansSuccess &&
      tvodRentals?.tvodPlans?.length
    ) {
      const TVODRentalsData = await fetchRentals();
      if (TVODRentalsData) {
        setTvodRentals({ ...TVODRentalsData, getPlansSuccess: true });
      }
    }
  };

  const getThumbnailType = () => {
    return ['tv-shows', 'web-series', 'news'].includes(contentType)
      ? ThumbnailTypes.upnext
      : ThumbnailTypes.vertical;
  };
  const { similarContents, recommendedContents, storiesContents } = railsData;

  const consumptionMetaDataProps = useMemo(
    () =>
      generateMetaDataProps(
        contentType,
        singlePlayDetailsMovie?.assetDetails?.id
          ? {
              ...getContentLiteDataFromSinglePlayData(
                singlePlayDetailsMovie || {}
              )
            }
          : contentLiteData,
        translations,
        undefined,
        similarContents,
        displayLanguage === DisplayLanguage.ENGLISH
          ? router.asPath
          : `/${router.locale}${router.asPath}`
      ),
    [
      similarContents,
      contentType,
      translations,
      contentLiteData,
      singlePlayDetailsMovie?.assetDetails
    ]
  );
  // const {adDetails} = singlePlayDetailsMovie;

  const totalEpisodes =
    similarContents?.assetSubType === 'original'
      ? similarContents?.seasons?.[0]?.episodes?.[
          similarContents?.seasons?.[0]?.episodes?.length - 1
        ].episodeNumber
      : similarContents?.seasons?.[0]?.totalEpisodes;

  let episodeIntervalValue = useMemo(
    () =>
      getAllEpisodeIntervals({
        totalEpisodes,
        baseEpisodeNumber: isOnAirShow ? 10 : 1,
        episodesString: 'Episodes'
      }),
    [totalEpisodes]
  );

  if (
    contentType === ContentTypes.webSeries &&
    similarContents?.seasons?.length > 1
  ) {
    episodeIntervalValue = similarContents?.seasons
      ?.map((season: any) => season?.title)
      ?.reverse();
  }

  const showDetailsObj =
    contentLiteData?.showDetails || singlePlayDetailsMovie?.showDetails;

  const getUpNext = async (params: UpnextParams) => {
    try {
      const contentAppropriateAge =
        getContentAppropriateAge(usersPreferenceData);
      params.pc_age = contentAppropriateAge;
      const getMovieUrl = gwapiUpNextUrlGenerator(params);
      const upNextData = await gwapigetWithUserToken(getMovieUrl);
      const upnextItems =
        upNextData?.items &&
        transformToCamelCase(upNextData?.items, newKeysList);
      return upnextItems;
    } catch (error) {
      console.error(
        'Something went wrong while fetching GWAPI upnext reco: ',
        error
      );
    }
  };

  const getContentForUpnext = () => {
    if (singlePlayDetailsMovie?.assetDetails?.asset_subtype === 'movie') {
      return singlePlayDetailsMovie?.assetDetails?.asset_subtype;
    } else {
      return singlePlayDetailsMovie?.showDetails?.asset_subtype;
    }
  };

  const getUpNextData = async () => {
    const params = {
      type: getContentForUpnext(),
      asset_id: contentIdFromUrl,
      translation: displayLanguages,
      languages: contentLanguages?.toString(),
      country: countryCode,
      series_id: singlePlayDetailsMovie?.assetDetails?.tvshow,
      season_id: singlePlayDetailsMovie?.assetDetails?.season
    };

    if (params && params.asset_id) {
      const upnextNewApi =
        isFeatureEnabled(
          remoteConfig,
          FeatureFlagConstants.FEATURE_UPNEXT_API
        ) || false;

      setUpNextApiState(false);
      setIsUpNextPending(true);
      if (upnextNewApi) {
        try {
          const data = (await getUpNext(params)) || [];
          setUpNextMovies(data);
        } catch (error) {
          setUpNextMovies([]);
        }
        setUpNextApiState(true);
        setIsUpNextPending(false);
      } else {
        setUpNextApiState(true);
        setIsUpNextPending(false);
        const data = recommendedContents?.recommendedContentData?.[1] || [];
        setUpNextMovies(data);
      }
    }
  };

  // Mixpanel Handling for consumption
  useEffect(() => {
    timeEvent();
  }, [contentLiteData || (!contentLiteData && consumptionMetaDataProps)]);

  useEffect(() => {
    getRentalsData();
  }, [tvodRentals, router.asPath]);

  useEffect(() => {
    if (tvodRentals?.tvodPlans && singlePlayDetailsMovie?.assetDetails) {
      const paymentJourney = getLocalStorage(PAYMENT_JOURNEY);
      if (paymentJourney === APP_USER) setPlexRentNowPopUp(true);
    }
  }, [tvodRentals, singlePlayDetailsMovie?.assetDetails]);

  // setPlayerSectionHeight
  useEffect(() => {
    setPlayerSectionHeight(playerSectionRef?.current?.offsetHeight);
  }, [playerSectionRef?.current?.offsetHeight, showCrew]);

  // handlers
  const commerceCloseIconHandler = useCallback(() => {
    setCommerceLayout('');
    commerceCTA(MIXPANEL_CLOSE);
  }, []);

  const handleBuyPlanPopup = () => {
    setBuyPlanPopup(!shouldShowBuyPlanPopup);
  };

  const handleSetParentalPinPopup = (status: boolean) => {
    if (typeof status === 'boolean') {
      setShowSetParentalPinPopup(status);
    }
  };

  const commerceDataStateLength = commerceData?.length;

  useEffect(() => {
    if (commerceDataStateLength) {
      setCommerceComponentData(commerceData?.[0]?.data);
    }
  }, [commerceDataStateLength]);

  const showWishlistHandler = useCallback(() => {
    handleCommerceLayout(true);
  }, []);

  const handleCommerceLayout = useCallback((fromWishlistHandler = false) => {
    if (typeof fromWishlistHandler === 'boolean' && fromWishlistHandler) {
      setShowWishlist(true);
    } else {
      setShowWishlist(false);
    }

    if (!isServer()) {
      if (window?.innerWidth >= DEVICE_WIDTH_CONSTANTS.deviceWidth1200) {
        setCommerceLayout(Commerce.SIDE_BAR);
      } else if (window?.innerWidth < DEVICE_WIDTH_CONSTANTS.deviceWidth1200) {
        setCommerceLayout(Commerce.DRAWER_POPUP);
      }
    }
  }, []);

  // handles close parent popup
  const closeParentalPoupup = () => {
    setShowSetParentalPinPopup(false);
  };

  useEffect(() => {
    if (
      usersPreferenceData?.parental_control_v2 &&
      getLocalStorage(USER_TOKEN) &&
      getAutomaticAgeRating(countrylist) &&
      singlePlayDetailsMovie?.error_code === '606'
    ) {
      getSinglePlayDetails();
    }
  }, [usersPreferenceData?.parental_control_v2]);

  useEffect(() => {
    if (
      singlePlayDetailsMovie &&
      singlePlayDetailsMovie?.assetDetails?.asset_subtype !==
        ASSET_SUBTYPE.TRAILER
    ) {
      urlAutoCorrectionOnClientSide(
        router,
        contentType,
        singlePlayDetailsMovie
      );
    }
  }, [singlePlayDetailsMovie]);

  // handles submit parent popup
  const onSubmitHandle = (status = false, parentalValue: string) => {
    setUsersPreferenceData({
      ...usersPreferenceData,
      parental_control_v2: parentalValue
    });
    setShowSetParentalPinPopup(status);
  };

  const companionAdsResponse = singlePlayDetailsMovie?.adDetails?.companion_ads
    ? singlePlayDetailsMovie?.adDetails?.companion_ads[0]
    : null;
  const companionAds = companionAdsResponse && companionAdsResponse?.ad_data[0];

  const notifyPlayingStarted = () => {
    setPlayingStarted(true);
    setTimeout(() => {
      if (!loadExternalScripts) {
        loadScripts(countryCode);
        setExternalScripts(true);
      }
      adsScriptGenerator();
    }, 7 * 1000);
  };

  const triggerAVODNudge = (isVisible = true) => {
    if (isVisible) {
      setPopups({ ...popups, isAvodPopupNudgeVisible: true });
    } else {
      setPopups({ ...popups, isAvodPopupNudgeVisible: false });
    }
  };
  const checkChromeVersionpopUp = (flag: boolean) => {
    setChromeVersionPopupEnabled(flag);
  };

  const seoMetaProps = {
    isConsumption: true,
    pageMeta: metaData,
    tag,
    liteData: contentLiteData,
    videoSchemaData: !(
      isKidsData &&
      (contentType === ContentTypes.tvshows ||
        contentType === ContentTypes.webSeries)
    )
      ? contentLiteData
      : undefined,
    movieSchemaData:
      contentType === ContentTypes.movies ? contentLiteData : undefined,
    tvEpisodeSchemaData:
      contentType === ContentTypes.tvshows ||
      contentType === ContentTypes.webSeries
        ? singlePlayDetailsMovie?.assetDetails || contentLiteData
        : undefined,
    tvSeriesSchemaShowDetailData: contentLiteData,
    tvSeriesSchemaEpisodeData: contentLiteData,
    televisionChannelSchemaData: true,
    apiData: contentLiteData,
    // allLanguages
    searchSchemaData: true,
    //need to be changed
    trailerData: contentLiteData,
    licenseData: {},
    railData: recommendedContents?.recommendedContentData?.[0]?.contents || [],
    itemListSchema: contentLiteData,
    webPageSchemaData:
      contentType === ContentTypes.movies ? contentLiteData : undefined,
    header: contentLiteData
      ? getHeaderForSEO(contentLiteData, contentType)
      : '',
    tableSchema: true,
    consumptionData: contentLiteData,
    breadcrumbs: getBreadCrumbs({
      contentLiteData: singlePlayDetailsMovie?.assetDetails || contentLiteData,
      consumptionPageTranslations,
      pageDetails,
      isKidsData,
      router,
      contentTitle,
      showName,
      showId
    }),
    seoMetaObj: seoMetaObject,
    keywords: contentLiteData?.extended?.meta_keywords,
    canonicalUrl: seoMetaObject?.canonicalUrl
  };

  const memoizedRecommendedContents = useMemo(
    () => recommendedContents,
    [recommendedContents]
  );
  const memoizedRecommendedContentsForEpisodes = useMemo(
    () => [memoizedRecommendedContents],
    [memoizedRecommendedContents]
  );

  const getRecommendedContentsByType = useCallback(() => {
    return recommendedContents?.thumbnailType === ThumbnailTypes?.upnext
      ? memoizedRecommendedContentsForEpisodes
      : memoizedRecommendedContents;
  }, [
    memoizedRecommendedContents,
    memoizedRecommendedContentsForEpisodes,
    contentType
  ]);

  if (
    !contentLiteData &&
    !singlePlayDetailsMovie?.assetDetails &&
    spAPIState === SPAPIStateEnum.FINISHED
  ) {
    if (isContentUnavailable) {
      return (
        <Fallback type={Fallback_Type_Constants.CONTENT_NOT_AVAILABLE_REGION} />
      );
    }
    return <Fallback type={Fallback_Type_Constants.NOT_AVAILABLE} />;
  }

  return (
    <>
      {!isRequestedByBot && <PlayerLoadingScripts />}
      <ConsumptionWrapper onClickCapture={clearSubscriptionNudgePopupTimer}>
        {singlePlayDetailsMovie &&
        Object.keys(singlePlayDetailsMovie).length > 0 ? (
          <ChromeVersionPopup
            videoDetails={singlePlayDetailsMovie}
            tvodRentals={tvodRentals}
            checkChromeVersionpopUp={checkChromeVersionpopUp}
          />
        ) : null}
        {isLatestRoute || isLatest1Route ? (
          <SEOMetaForLatest
            title={metaData?.title}
            description={metaDataType?.description}
          />
        ) : (
          <SEOMeta {...seoMetaProps} />
        )}

        {!isServer() && consumptionPageAudioLanguages && (
          <LanguageChangePopup audioLanguages={consumptionPageAudioLanguages} />
        )}

        <ConsumptionComponentContainer>
          <MainSection>
            <PlayerAndMetaData ref={playerSectionRef}>
              <PlayerWrapper
                data-testid="playerWrapper"
                isRequestedByBot={isRequestedByBot}>
                <PlayerPlaceholder
                  isRequestedBySearchBot={isRequestedByBot}
                  assetDetails={
                    {
                      id: contentLiteData?.id,
                      image: contentLiteData?.image,
                      assetType: contentLiteData?.asset_type,
                      title: contentLiteData?.title,
                      asset_type: contentLiteData?.asset_type
                    } as any
                  }>
                  <VideoPlayer
                    contentId={contentIdFromUrl as string}
                    primaryMediaId={contentIdFromUrl}
                    country={countryCode}
                    countryList={countrylist}
                    singlePlayDetails={singlePlayDetailsMovie}
                    getSinglePlayDetails={getSinglePlayDetails}
                    upNextApiState={upNextApiState}
                    isUpNextPending={isUpNextPending}
                    getUpNextData={getUpNextData}
                    similarFromReco={upNextMovies}
                    displayLanguage={displayLanguages}
                    // Make below attributes optional
                    setVITvod={setVITvod}
                    callStaledApis={notifyPlayingStarted}
                    handleParentalPin={handleSetParentalPinPopup}
                    setAvodPopupNudgeVisibleAction={triggerAVODNudge}
                    plansMetaData={
                      plansMetaData?.length ? plansMetaData : retryPlansMetaData
                    }
                    subscriptionPlansPending={undefined}
                    subscriptionPlansError={undefined}
                    tvodState={{ ...tvodState, ...comboOfferState }}
                    toggleTeaserSubscriptionPopup={
                      toggleTeaserSubscriptionPopup
                    }
                    userType={appConfig.userType}
                    handleRentNowPopUp={handleRentNowPopUp}
                    tvodRentals={tvodRentals}
                    remoteConfig={remoteConfig}
                    userData={usersData}
                    handleCommerceLayout={handleCommerceLayout}
                  />
                </PlayerPlaceholder>
              </PlayerWrapper>
              {contentLiteData?.asset_type || consumptionMetaDataProps ? (
                <MetaDataContainer
                  playerWrapperHeight={playerWrapperHeight}
                  isRequestedByBot={isRequestedByBot}
                  isGuestUser={userType === USER_TYPES.GUEST}>
                  {tvodState?.isTVODLogicResolved &&
                  !tvodState?.isUserEntitledForTvod &&
                  comboOfferState?.isComboOfferEnabled &&
                  contentId &&
                  !isGlobalCheck(router.asPath) ? (
                    <ComboOfferWidget id={contentId} />
                  ) : null}
                  {deviceType === DEVICE_TYPES.MOBILE &&
                  companionAds &&
                  Object.keys(companionAds).length &&
                  companionAdState.isAdPlayingOnPlayer ? (
                    <AdComponent
                      key={companionAds?.div_id}
                      adUnit={companionAds?.ad_tag}
                      id={companionAds?.div_id}
                      dimension={companionAds?.ad_dimension}
                      adTarget={companionAds?.ad_targeting}
                      type="companionAd"
                      position={0}
                      pageType="consumption"
                    />
                  ) : null}
                  {contentLiteData?.asset_type ||
                  singlePlayDetailsMovie?.assetDetails?.id ? (
                    <ConsumptionMetaData
                      {...consumptionMetaDataProps}
                      getSinglePlayDetails={getSinglePlayDetails}
                      singlePlayData={singlePlayDetailsMovie}
                      showCrew={showCrew}
                      toggleCrew={toggleCrew}
                      showWishlist={showWishlistHandler}
                      handleCommerceLayout={handleCommerceLayout}
                    />
                  ) : (
                    <ConsumptionMetaDataSkeletonLoader />
                  )}
                </MetaDataContainer>
              ) : null}
            </PlayerAndMetaData>
            <RecommendedContentsWrapper
              companionAds={companionAds as CompanionAdsData}
              recommendedContents={memoizedRecommendedContents}
              sectionHeight={playerSectionHeight}
              assetDetails={
                singlePlayDetailsMovie?.assetDetails as AssetDetails
              }
              status={railsData.status}
              contentType={contentType}
              commerceLayout={commerceLayout}
              showWishlist={showWishlist}
              showWishlistHandler={showWishlistHandler}
              handleCommerceLayout={handleCommerceLayout}
              commerceCloseIconHandler={commerceCloseIconHandler}
            />
          </MainSection>

          <Container>
            {tvodState?.isTvodContent && !tvodState?.isTvodEvent ? (
              <PlexKnowMoreWidget />
            ) : null}
            {(railsData?.similarContents &&
              railsData.status === API_STATUS_TYPES.success) ||
            (railsData?.otherCollection?.length && isRequestedByBot) ? (
              <RelatedContents
                contentInstance={contentInstance as Rails}
                type={getThumbnailType()}
                recommendedContents={getRecommendedContentsByType()}
                similarContents={similarContents}
                otherCollections={railsData?.otherCollection}
                dropdownOptions={episodeIntervalValue}
                storyRailsData={storiesContents}
                tvShowTitle={showDetailsObj?.title || ''}
                seasonLink={showMoreClickHandler(
                  singlePlayDetailsMovie,
                  router?.asPath,
                  router,
                  true,
                  similarContents?.seasons
                )}
                singlePlayDetailsMovie={singlePlayDetailsMovie}
              />
            ) : (
              <SkeletonSimilarContentTray />
            )}
            {!isLatestRoute && !isLatest1Route ? (
              contentLiteData &&
              (contentType === ContentTypes.movies ||
                contentType === ContentTypes.tvshows ||
                contentType === ContentTypes.webSeries) ? (
                <>
                  <SeoContent
                    seoData={contentLiteData}
                    pageType={getConsumptionPageType(contentType)}
                    header={
                      contentLiteData &&
                      getHeaderForSEO(contentLiteData, contentType)
                    }
                    showOrWebSeries={determineShowOrWebSeries(showDetailsObj)}
                  />
                  {
                    // to be shown for following consumption pages movies, news, channel, music
                    contentType === ContentTypes.movies ? (
                      <SeoDescription
                        seoDescription={
                          contentLiteData?.extended?.seo_content_block ||
                          contentLiteData.extended_wh?.seo_content_block ||
                          ''
                        }
                      />
                    ) : null
                  }
                </>
              ) : (
                <>
                  <SEOContentSkeletonLoader />
                  <SEODescriptionSkeletonLoader />
                </>
              )
            ) : null}
          </Container>
        </ConsumptionComponentContainer>
        <ScrollToTopButton />

        {shouldShowBuyPlanPopup && (
          <ConsumptionBuyPlanPopup
            showBuyPlanFlag={shouldShowBuyPlanPopup}
            closeHandler={handleBuyPlanPopup}
          />
        )}
        {showSetParentalPinPopup ? (
          <SetUpParentalPin submitHandle={onSubmitHandle} />
        ) : null}

        {contentLiteData && (
          <TvodController
            singlePlayDetailsMovie={
              singlePlayDetailsMovie as SinglePlayResponse
            }
            contentLiteData={contentLiteData}
            contentIdFromUrl={contentIdFromUrl}
            showPlexRentNowPopUp={showPlexRentNowPopUp}
            setPlexRentNowPopUp={setPlexRentNowPopUp}
          />
        )}

        {commerceLayout === Commerce.DRAWER_POPUP && (
          <CommerceDrawerPopUp
            showWishlist={showWishlist}
            playerWrapperHeight={playerWrapperHeight}
            handleCommerceLayout={handleCommerceLayout}
            closeIconClick={commerceCloseIconHandler}
          />
        )}

        {popups.isAvodPopupNudgeVisible && !isRequestedByBot && (
          <SubscriptionAVODNudge handleBuyPlan={setBuyPlanPopup} />
        )}
      </ConsumptionWrapper>
    </>
  );
};

export default ConsumptionPage;
